import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { DatePicker } from "../components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Loader2,
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Building2,
  FileText,
  Save,
  Edit2,
  Trash2,
  Plus,
  Shield,
  Clock,
  IndianRupee,
  AlertTriangle,
  Check,
  X,
  Download,
  UserPlus,
  Upload,
  File,
  FileImage,
  FileSpreadsheet,
  Eye,
  MoreVertical,
  KeyRound,
  RefreshCw,
  Copy,
  Share2,
  Link
} from "lucide-react";
import { authGet, authPost, authPut, authDelete, authUpload, getAuthToken } from "../utils/auth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorClientDetail() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [client, setClient] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [showAddPlatform, setShowAddPlatform] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState("");
  const [addingPlatform, setAddingPlatform] = useState(false);
  const [showRenamePlatform, setShowRenamePlatform] = useState(false);
  const [renamePlatformName, setRenamePlatformName] = useState("");
  const [formData, setFormData] = useState({});
  
  // Document states
  const [documents, setDocuments] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [docDescription, setDocDescription] = useState("");
  
  // Policy modal states
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    insurance_company: "",
    plan_name: "",
    policy_number: "",
    policy_type: "Fresh", // Fresh, Portability, Renewal
    sum_insured: "",
    premium_amount: "",
    policy_start_date: "",
    policy_end_date: "",
    payment_date: "", // Payment date for contest calculation
    policy_term_years: 1, // 1, 2, or 3 year policy
    payment_frequency: "Annual",
    platform: "",
    referred_by: "",
    associate_name: "",
    family_members: "",
    notes: ""
  });
  
  // Referral source options
  const referralSources = ["Direct", "Beshak"];

  const [savingPolicy, setSavingPolicy] = useState(false);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(null);
  
  // Client Portal PIN states
  const [portalPin, setPortalPin] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinCopied, setPinCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Client Portal PIN functions
  const fetchPortalPin = async () => {
    try {
      const res = await authGet(`/api/advisor/client/${clientId}/portal-pin`);
      if (res.ok) {
        setPortalPin(res.data?.pin || null);
      }
    } catch (err) {
      console.error("Failed to fetch portal PIN:", err);
    }
  };

  const generatePortalPin = async () => {
    setPinLoading(true);
    try {
      const res = await authPost(`/api/advisor/client/${clientId}/generate-pin`, {});
      if (res.ok) {
        setPortalPin(res.data.pin);
        toast.success(`PIN generated: ${res.data.pin}`);
      } else {
        toast.error(res.data?.detail || "Failed to generate PIN");
      }
    } catch (err) {
      toast.error("Failed to generate PIN");
    } finally {
      setPinLoading(false);
    }
  };

  const copyPin = () => {
    if (portalPin) {
      navigator.clipboard.writeText(portalPin);
      setPinCopied(true);
      toast.success("PIN copied!");
      setTimeout(() => setPinCopied(false), 2000);
    }
  };

  const copyPortalLink = () => {
    if (portalPin) {
      const link = `${window.location.origin}/client/login?pin=${portalPin}&client=${clientId}`;
      navigator.clipboard.writeText(link);
      setLinkCopied(true);
      toast.success("Portal link copied!");
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const sharePortalLink = () => {
    if (portalPin && navigator.share) {
      const link = `${window.location.origin}/client/login?pin=${portalPin}&client=${clientId}`;
      navigator.share({ title: `Insurance Dashboard - ${client?.name}`, text: `View your insurance dashboard. PIN: ${portalPin}`, url: link });
    } else {
      copyPortalLink();
    }
  };

  useEffect(() => {
    // Check auth via token
    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        navigate("/advisor/login");
        return;
      }
      try {
        const response = await authGet('/api/auth/me');
        if (!response.ok) {
          navigate("/advisor/login");
          return;
        }
        fetchClientData();
        fetchPlatforms();
        fetchPortalPin();
        fetchDocuments();
      } catch (err) {
        navigate("/advisor/login");
      }
    };
    checkAuth();
  }, [clientId, navigate]);

  const fetchClientData = async () => {
    setLoading(true);
    
    try {
      const response = await authGet(`/api/advisor/clients/${clientId}`);
      
      if (response.status === 401) {
        navigate("/advisor/login");
        return;
      }
      
      if (response.status === 404) {
        toast.error("Client not found");
        navigate("/advisor/clients");
        return;
      }
      
      const data = response.data;
      setClient(data.client);
      setPolicies(data.policies || []);
      setFormData(data.client);
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await authGet(`/api/advisor/clients/${clientId}/documents`);
      if (response.ok) {
        setDocuments(response.data?.documents || []);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingDocs(true);
    
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      formData.append('description', docDescription);
      
      const response = await authUpload(`/api/advisor/clients/${clientId}/documents`, formData);
      
      if (response.ok) {
        toast.success(`${response.data.documents?.length || 0} document(s) uploaded successfully`);
        fetchDocuments();
        setShowDocumentModal(false);
        setDocDescription("");
      } else {
        toast.error(response.data?.detail || "Failed to upload documents");
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Failed to upload documents");
    } finally {
      setUploadingDocs(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/advisor/clients/${clientId}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        toast.error("Failed to download document");
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const downloadPolicyDoc = async (policy) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/advisor/policies/${policy.id}/document`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        if (res.status === 404) toast.error("This policy's PDF is missing. Use the upload button to re-attach it.");
        else toast.error("Could not download the policy document.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(policy.insurance_company || "policy").replace(/\s+/g, "_")}_${policy.policy_number || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Could not download the policy document.");
    }
  };

  const uploadPolicyDoc = async (policy, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Please choose a PDF file"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Keep the PDF under 25 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    toast.info("Uploading policy document…");
    const res = await authUpload(`/api/advisor/policies/${policy.id}/document`, fd);
    if (res.ok) { toast.success("Policy document uploaded"); fetchClientData(); }
    else { toast.error(res.data?.detail || "Upload failed. Please try again."); }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    try {
      const response = await authDelete(`/api/advisor/clients/${clientId}/documents/${docId}`);
      if (response.ok) {
        toast.success("Document deleted");
        fetchDocuments();
      } else {
        toast.error("Failed to delete document");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  const getDocumentIcon = (fileType) => {
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType)) return FileImage;
    if (['xls', 'xlsx'].includes(fileType)) return FileSpreadsheet;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const fetchPlatforms = async () => {
    try {
      const response = await authGet('/api/advisor/platforms');
      setPlatforms(response.data?.platforms || []);
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveClient = async () => {
    if (!formData.name?.trim() || !formData.mobile?.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await authPut(`/api/advisor/clients/${clientId}`, formData);
      
      if (response.ok) {
        setClient(response.data.client);
        setEditMode(false);
        toast.success("Client updated successfully");
      } else {
        toast.error("Failed to update client");
      }
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      const response = await authDelete(`/api/advisor/clients/${clientId}`);
      
      if (response.ok) {
        toast.success("Client deleted successfully");
        navigate("/advisor/clients");
      } else {
        toast.error("Failed to delete client");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Failed to delete client");
    }
  };

  // Policy handlers
  const handlePolicyChange = (e) => {
    const { name, value } = e.target;
    setPolicyForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePlatformChange = (value) => {
    setPolicyForm(prev => ({ ...prev, platform: value }));
  };

  const handleAddPlatform = async () => {
    if (!newPlatformName.trim()) { toast.error("Enter platform name"); return; }
    setAddingPlatform(true);
    try {
      const res = await authPost('/api/advisor/platforms', { name: newPlatformName.trim() });
      if (!res.ok) throw new Error(res.data?.detail || "Failed to add");
      setPlatforms(prev => [...prev, newPlatformName.trim()]);
      setPolicyForm(prev => ({ ...prev, platform: newPlatformName.trim() }));
      setNewPlatformName("");
      setShowAddPlatform(false);
      toast.success("Platform added");
    } catch (e) { toast.error(e.message); }
    setAddingPlatform(false);
  };

  const handleDeletePlatform = async (name) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/advisor/platforms/${encodeURIComponent(name)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setPlatforms(prev => prev.filter(p => p !== name));
        if (policyForm.platform === name) setPolicyForm(prev => ({ ...prev, platform: "" }));
        toast.success(`"${name}" removed`);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Failed to delete");
      }
    } catch (e) { toast.error("Failed to delete platform"); }
  };

  const handleRenamePlatform = async () => {
    const oldName = policyForm.platform;
    const newName = renamePlatformName.trim();
    if (!newName) { toast.error("Enter new platform name"); return; }
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/advisor/platforms/${encodeURIComponent(oldName)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_name: newName })
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setPlatforms(prev => prev.map(p => (p === oldName ? newName : p)));
        setPolicyForm(prev => ({ ...prev, platform: newName }));
        setShowRenamePlatform(false);
        setRenamePlatformName("");
        toast.success(`Renamed to "${newName}"`);
      } else toast.error(d.detail || "Failed to rename");
    } catch (e) { toast.error("Failed to rename platform"); }
  };

  const handleFrequencyChange = (value) => {
    setPolicyForm(prev => ({ ...prev, payment_frequency: value }));
  };

  const handleReferredByChange = (value) => {
    setPolicyForm(prev => ({ 
      ...prev, 
      referred_by: value,
      associate_name: ''
    }));
  };

  // Calculate policy term from dates
  const calculatePolicyTerm = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    const diffYears = diffMs / (365.25 * 24 * 60 * 60 * 1000);
    return Math.max(1, Math.round(diffYears));
  };

  // Handle date change and auto-calculate policy term
  const handleDateChange = (field, value) => {
    setPolicyForm(prev => {
      const newForm = { ...prev, [field]: value };
      // Auto-calculate policy term when both dates are set
      if (newForm.policy_start_date && newForm.policy_end_date) {
        newForm.policy_term_years = calculatePolicyTerm(newForm.policy_start_date, newForm.policy_end_date);
      }
      // Auto-set payment_date to policy_start_date when start date changes (but keep it editable)
      if (field === 'policy_start_date' && value && !prev.payment_date) {
        newForm.payment_date = value;
      }
      return newForm;
    });
  };

  const handlePolicyTermChange = (value) => {
    setPolicyForm(prev => ({ ...prev, policy_term_years: parseInt(value) }));
  };

  const openAddPolicyModal = () => {
    setEditingPolicy(null);
    setPolicyForm({
      insurance_company: "",
      plan_name: "",
      policy_number: "",
      policy_type: "Fresh",
      sum_insured: "",
      premium_amount: "",
      policy_start_date: "",
      policy_end_date: "",
      payment_date: "",
      policy_term_years: 1,
      payment_frequency: "Annual",
      platform: "",
      referred_by: "",
      associate_name: "",
      family_members: "",
      notes: ""
    });
    setShowPolicyModal(true);
  };

  const openEditPolicyModal = (policy) => {
    setEditingPolicy(policy);
    // Calculate policy term from dates if not stored
    const policyTerm = policy.policy_term_years || calculatePolicyTerm(policy.policy_start_date, policy.policy_end_date);
    setPolicyForm({
      insurance_company: policy.insurance_company || "",
      plan_name: policy.plan_name || "",
      policy_number: policy.policy_number || "",
      policy_type: policy.policy_type || "Fresh",
      sum_insured: policy.sum_insured || "",
      premium_amount: policy.premium_amount || "",
      policy_start_date: policy.policy_start_date || "",
      policy_end_date: policy.policy_end_date || "",
      payment_date: policy.payment_date || "",
      policy_term_years: policyTerm,
      payment_frequency: policy.payment_frequency || "Annual",
      platform: policy.platform || "",
      referred_by: policy.referred_by || "",
      associate_name: policy.associate_name || "",
      family_members: (policy.family_members || []).join(", "),
      notes: policy.notes || ""
    });
    setShowPolicyModal(true);
  };

  const handleSavePolicy = async () => {
    if (!policyForm.insurance_company || !policyForm.plan_name || !policyForm.policy_number) {
      toast.error("Insurance company, plan name, and policy number are required");
      return;
    }
    
    setSavingPolicy(true);
    
    const payload = {
      ...policyForm,
      client_id: clientId,
      family_members: policyForm.family_members 
        ? policyForm.family_members.split(",").map(s => s.trim()).filter(Boolean)
        : []
    };
    
    try {
      let response;
      if (editingPolicy) {
        response = await authPut(`/api/advisor/policies/${editingPolicy.id}`, payload);
      } else {
        response = await authPost('/api/advisor/policies', payload);
      }
      
      if (response.ok) {
        toast.success(editingPolicy ? "Policy updated successfully" : "Policy added successfully");
        setShowPolicyModal(false);
        fetchClientData();
      } else if (response.status === 409) {
        // Duplicate policy detected
        const errorData = await response.json();
        toast.error(errorData.detail || "Duplicate policy detected! This policy already exists in the system.", {
          duration: 8000,  // Show for longer since it's an important message
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to save policy");
      }
    } catch (error) {
      console.error("Error saving policy:", error);
      toast.error("Failed to save policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleDeletePolicy = async () => {
    if (!deletingPolicy) return;
    
    try {
      const response = await authDelete(`/api/advisor/policies/${deletingPolicy.id}`);
      
      if (response.ok) {
        toast.success("Policy deleted successfully");
        setShowDeleteConfirm(false);
        setDeletingPolicy(null);
        fetchClientData();
      } else {
        toast.error("Failed to delete policy");
      }
    } catch (error) {
      console.error("Error deleting policy:", error);
      toast.error("Failed to delete policy");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntilRenewal = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  };

  const getPolicyStatus = (endDate) => {
    const days = getDaysUntilRenewal(endDate);
    if (days === null) return { status: "unknown", color: "slate" };
    if (days < 0) return { status: "Expired", color: "red" };
    if (days <= 7) return { status: `${days} days left`, color: "red" };
    if (days <= 30) return { status: `${days} days left`, color: "amber" };
    return { status: "Active", color: "emerald" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#3A4557]">Loading client data...</p>
        </div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      {/* Header */}
      <header className="bg-[#EDF1F8]/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/advisor/clients")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center">
                <User className="h-5 w-5 text-[#2E3B57]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1B2A4A]">{client.name}</h1>
                <p className="text-[#3A4557] text-sm">{policies.length} policies</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {!editMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="border-red-500/50 text-red-600 hover:text-red-700 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFormData(client);
                      setEditMode(false);
                    }}
                    className="text-[#3A4557] hover:text-[#1B2A4A]"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveClient}
                    disabled={saving}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Client Info Card */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-[#1B2A4A] mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-[#2E3B57]" />
              Client Information
            </h2>
            
            {editMode ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#3A4557] text-sm">Full Name *</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name || ""}
                    onChange={handleChange}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                    data-testid="edit-client-name"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Mobile *</Label>
                  <Input
                    type="tel"
                    name="mobile"
                    value={formData.mobile || ""}
                    onChange={handleChange}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                    data-testid="edit-client-mobile"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Email</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm mb-1 block">Date of Birth</Label>
                  <DatePicker
                    date={formData.dob}
                    onDateChange={(date) => setFormData(prev => ({ ...prev, dob: date }))}
                    placeholder="Select date of birth"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">City</Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city || ""}
                    onChange={handleChange}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Address</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address || ""}
                    onChange={handleChange}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[#3A4557] text-sm">Notes</Label>
                  <Textarea
                    name="notes"
                    value={formData.notes || ""}
                    onChange={handleChange}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] min-h-[80px]"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-[#3A4557] text-sm flex items-center gap-1"><Phone className="h-3 w-3" /> Mobile</p>
                  <p className={`font-medium ${client.mobile ? 'text-[#1B2A4A]' : 'text-[#A87608] italic'}`}>
                    {client.mobile || 'Fill manually'}
                  </p>
                </div>
                <div>
                  <p className="text-[#3A4557] text-sm flex items-center gap-1"><Mail className="h-3 w-3" /> Email</p>
                  <p className={`font-medium ${client.email ? 'text-[#1B2A4A]' : 'text-[#A87608] italic'}`}>
                    {client.email || 'Fill manually'}
                  </p>
                </div>
                {client.dob && (
                  <div>
                    <p className="text-[#3A4557] text-sm flex items-center gap-1"><Calendar className="h-3 w-3" /> DOB</p>
                    <p className="text-[#1B2A4A] font-medium">{formatDate(client.dob)}</p>
                  </div>
                )}
                {client.city && (
                  <div>
                    <p className="text-[#3A4557] text-sm flex items-center gap-1"><Building2 className="h-3 w-3" /> City</p>
                    <p className="text-[#1B2A4A] font-medium">{client.city}</p>
                  </div>
                )}
                {client.address && (
                  <div className="col-span-2">
                    <p className="text-[#3A4557] text-sm flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</p>
                    <p className="text-[#1B2A4A] font-medium">{client.address}</p>
                  </div>
                )}
                {client.notes && (
                  <div className="col-span-full">
                    <p className="text-[#3A4557] text-sm flex items-center gap-1"><FileText className="h-3 w-3" /> Notes</p>
                    <p className="text-[#2E3B57]">{client.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Portal Access Section */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-8" data-testid="portal-access-card">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-[#1B2A4A] mb-4 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#A87608]" />
              Client Portal Access
            </h2>
            
            {portalPin ? (
              <div className="space-y-4">
                {/* PIN Display */}
                <div className="bg-[#EDF1F8]/60 rounded-xl p-4 border border-[#C9D4E6]">
                  <p className="text-[#3A4557] text-xs mb-2">Current Portal PIN</p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {portalPin.split("").map((digit, i) => (
                        <div key={i} className="w-10 h-12 rounded-lg bg-[#D4A017]/10 border border-[#D4A017]/30 flex items-center justify-center">
                          <span className="text-xl font-bold text-[#A87608]">{digit}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyPin}
                        className="text-[#3A4557] hover:text-[#1B2A4A]"
                        data-testid="copy-pin-btn"
                      >
                        {pinCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={generatePortalPin}
                    disabled={pinLoading}
                    variant="outline"
                    className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                    data-testid="regenerate-pin-btn"
                  >
                    {pinLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    New PIN
                  </Button>
                  <Button
                    onClick={sharePortalLink}
                    className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0D1A3A] font-bold"
                    data-testid="share-portal-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Link
                  </Button>
                </div>
                
                {/* Copy Link */}
                <button
                  onClick={copyPortalLink}
                  className="w-full text-left bg-[#EDF1F8]/40 rounded-lg p-3 border border-[#C9D4E6]/50 hover:bg-[#EDF1F8]/60 transition-colors group"
                  data-testid="copy-link-btn"
                >
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4 text-[#3A4557] group-hover:text-[#A87608]" />
                    <span className="text-xs text-[#3A4557] truncate flex-1">
                      {window.location.origin}/client/login?pin={portalPin}&client={clientId}
                    </span>
                    {linkCopied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3 text-[#3A4557]" />}
                  </div>
                </button>
              </div>
            ) : (
              <div className="text-center py-4">
                <KeyRound className="h-10 w-10 mx-auto mb-3 text-[#3A4557]" />
                <p className="text-[#3A4557] text-sm mb-1">No portal access set up yet</p>
                <p className="text-[#3A4557] text-xs mb-4">Generate a 4-digit PIN so your client can view their insurance dashboard</p>
                <Button
                  onClick={generatePortalPin}
                  disabled={pinLoading}
                  className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold"
                  data-testid="generate-pin-btn"
                >
                  {pinLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                  Generate Client Portal PIN
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Policies Section */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#1B2A4A] flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#A87608]" />
            Policies ({policies.length})
          </h2>
          <Button
            onClick={openAddPolicyModal}
            className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold rounded-full shadow-lg shadow-[#D4A017]/15"
            data-testid="add-policy-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Policy
          </Button>
        </div>

        {policies.length === 0 ? (
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardContent className="p-12 text-center">
              <Shield className="h-16 w-16 mx-auto mb-4 text-[#3A4557]" />
              <h3 className="text-xl font-semibold text-[#1B2A4A] mb-2">No Policies Yet</h3>
              <p className="text-[#3A4557] mb-6">Add the client's first insurance policy.</p>
              <Button
                onClick={openAddPolicyModal}
                className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Policy
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {policies.map((policy) => {
              const statusInfo = getPolicyStatus(policy.policy_end_date);
              return (
                <Card
                  key={policy.id}
                  className="bg-white/50 border-[#C9D4E6] hover:border-[#C9D4E6] transition-colors"
                  data-testid={`policy-card-${policy.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-[#1B2A4A]">{policy.plan_name}</h3>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                policy.policy_type === 'Fresh' ? 'bg-emerald-500/20 text-emerald-600' :
                                policy.policy_type === 'Portability' ? 'bg-purple-500/20 text-purple-600' :
                                'bg-[#EDF1F8]/40 text-[#2E3B57]'
                              }`}>
                                {policy.policy_type || 'Fresh'}
                              </span>
                              {policy.renewal_status === 'Renewed' && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-600" data-testid={`renewed-badge-${policy.id}`}>
                                  Renewed
                                </span>
                              )}
                            </div>
                            <p className="text-[#A87608] font-medium">{policy.insurance_company}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${statusInfo.color}-500/20 text-${statusInfo.color}-700`}>
                            {statusInfo.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                          <div>
                            <p className="text-[#3A4557]">Policy No.</p>
                            <p className="text-[#1B2A4A] font-medium">{policy.policy_number}</p>
                          </div>
                          <div>
                            <p className="text-[#3A4557]">Sum Insured</p>
                            <p className="text-[#1B2A4A] font-medium flex items-center gap-1">
                              <IndianRupee className="h-3 w-3" />{policy.sum_insured}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#3A4557]">Premium</p>
                            <p className="text-[#1B2A4A] font-medium flex items-center gap-1">
                              <IndianRupee className="h-3 w-3" />{policy.premium_amount}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#3A4557]">Renewal</p>
                            <p className="text-[#1B2A4A] font-medium">{formatDate(policy.policy_end_date)}</p>
                          </div>
                        </div>
                        
                        {policy.payment_date && (
                          <div className="mt-3 text-sm">
                            <span className="text-[#3A4557]">Payment Date: </span>
                            <span className="text-[#A87608]">{formatDate(policy.payment_date)}</span>
                          </div>
                        )}
                        
                        {policy.platform && (
                          <div className="mt-3 text-sm">
                            <span className="text-[#3A4557]">Platform: </span>
                            <span className="text-[#2E3B57]">{policy.platform}</span>
                          </div>
                        )}
                        
                        {policy.family_members?.length > 0 && (
                          <div className="mt-2 text-sm">
                            <span className="text-[#3A4557]">Members: </span>
                            <span className="text-[#2E3B57]">{policy.family_members.join(", ")}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex sm:flex-col gap-2">
                        {policy.policy_document_path && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadPolicyDoc(policy)}
                            className="border-green-500/50 text-green-600 hover:text-green-700 hover:bg-green-500/10"
                            title="Download Policy PDF"
                            data-testid={`policy-download-${policy.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <label
                          className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8] cursor-pointer"
                          title={policy.policy_document_path ? "Replace policy PDF" : "Upload policy PDF"}
                          data-testid={`policy-upload-label-${policy.id}`}
                        >
                          <Upload className="h-4 w-4" />
                          <input type="file" accept=".pdf" className="hidden"
                            onChange={(e) => { uploadPolicyDoc(policy, e.target.files?.[0]); e.target.value = ""; }}
                            data-testid={`policy-upload-${policy.id}`} />
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditPolicyModal(policy)}
                          className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDeletingPolicy(policy);
                            setShowDeleteConfirm(true);
                          }}
                          className="border-red-500/50 text-red-600 hover:text-red-700 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Client Documents Section */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#1B2A4A] flex items-center gap-2">
            <File className="h-5 w-5 text-[#2E3B57]" />
            Documents ({documents.length})
          </h2>
          <Button
            onClick={() => setShowDocumentModal(true)}
            className="bg-[#EDF1F8] hover:bg-blue-700 text-white"
            size="sm"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Documents
          </Button>
        </div>

        {documents.length === 0 ? (
          <Card className="bg-white/50 border-[#C9D4E6] border-dashed">
            <CardContent className="py-8 text-center">
              <File className="h-12 w-12 text-[#3A4557] mx-auto mb-3" />
              <p className="text-[#3A4557] mb-4">No documents uploaded yet</p>
              <Button
                onClick={() => setShowDocumentModal(true)}
                variant="outline"
                className="border-blue-500/50 text-[#2E3B57] hover:bg-[#EDF1F8]/30"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload First Document
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {documents.map((doc) => {
              const DocIcon = getDocumentIcon(doc.file_type);
              return (
                <Card key={doc.id} className="bg-white/50 border-[#C9D4E6] hover:border-blue-500/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                        <DocIcon className="h-5 w-5 text-[#2E3B57]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1B2A4A] font-medium truncate" title={doc.original_name}>
                          {doc.original_name}
                        </p>
                        <p className="text-[#3A4557] text-xs">
                          {formatFileSize(doc.file_size)} • {doc.file_type.toUpperCase()}
                        </p>
                        <p className="text-[#3A4557] text-xs">
                          {new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc)}
                        className="flex-1 border-green-500/50 text-green-600 hover:text-green-700 hover:bg-green-500/10"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="border-red-500/50 text-red-600 hover:text-red-700 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Upload Document Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#2E3B57]" />
              Upload Documents
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              Upload policy documents, ID proofs, or any other client documents
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-[#3A4557] text-sm mb-2 block">Select Files</Label>
              <div className="border-2 border-dashed border-[#C9D4E6] rounded-lg p-6 text-center hover:border-blue-500/50 transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  onChange={handleDocumentUpload}
                  disabled={uploadingDocs}
                  className="hidden"
                  id="document-upload"
                />
                <label htmlFor="document-upload" className="cursor-pointer">
                  {uploadingDocs ? (
                    <>
                      <Loader2 className="h-8 w-8 text-[#2E3B57] mx-auto mb-2 animate-spin" />
                      <p className="text-[#2E3B57]">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-[#3A4557] mx-auto mb-2" />
                      <p className="text-[#2E3B57]">Click to select files</p>
                      <p className="text-[#3A4557] text-xs mt-1">PDF, Images, Word, Excel (Max 10MB each)</p>
                    </>
                  )}
                </label>
              </div>
            </div>
            
            <div>
              <Label className="text-[#3A4557] text-sm">Description (Optional)</Label>
              <Input
                type="text"
                value={docDescription}
                onChange={(e) => setDocDescription(e.target.value)}
                placeholder="e.g., Policy document, ID proof"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDocumentModal(false)}
              className="border-[#C9D4E6] text-[#2E3B57]"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Policy Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {editingPolicy ? "Edit Policy" : "Add New Policy"}
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              {editingPolicy ? "Update the policy details below" : "Enter the policy details for this client"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-[#3A4557] text-sm">Insurance Company *</Label>
              <Input
                type="text"
                name="insurance_company"
                value={policyForm.insurance_company}
                onChange={handlePolicyChange}
                placeholder="e.g., HDFC ERGO"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                data-testid="policy-company-input"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Plan Name *</Label>
              <Input
                type="text"
                name="plan_name"
                value={policyForm.plan_name}
                onChange={handlePolicyChange}
                placeholder="e.g., Optima Secure"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                data-testid="policy-plan-input"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Policy Number *</Label>
              <Input
                type="text"
                name="policy_number"
                value={policyForm.policy_number}
                onChange={handlePolicyChange}
                placeholder="Enter policy number"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
                data-testid="policy-number-input"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Policy Type *</Label>
              <Select value={policyForm.policy_type} onValueChange={(value) => setPolicyForm(prev => ({ ...prev, policy_type: value }))}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10" data-testid="policy-type-select">
                  <SelectValue placeholder="Select policy type..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  <SelectItem value="Fresh">Fresh Policy</SelectItem>
                  <SelectItem value="Portability">Port Policy (Portability)</SelectItem>
                  <SelectItem value="Renewal">Renewal Policy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Sum Insured</Label>
              <Input
                type="text"
                name="sum_insured"
                value={policyForm.sum_insured}
                onChange={handlePolicyChange}
                placeholder="e.g., 10 Lakhs"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Premium Amount</Label>
              <Input
                type="text"
                name="premium_amount"
                value={policyForm.premium_amount}
                onChange={handlePolicyChange}
                placeholder="e.g., 25000"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Payment Frequency</Label>
              <Select value={policyForm.payment_frequency} onValueChange={handleFrequencyChange}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  <SelectItem value="Annual">Annual</SelectItem>
                  <SelectItem value="Half-Yearly">Half-Yearly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm mb-1 block">Policy Start Date</Label>
              <DatePicker
                date={policyForm.policy_start_date}
                onDateChange={(date) => handleDateChange('policy_start_date', date)}
                placeholder="Select start date"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm mb-1 block">Policy End Date</Label>
              <DatePicker
                date={policyForm.policy_end_date}
                onDateChange={(date) => handleDateChange('policy_end_date', date)}
                placeholder="Select end date"
              />
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm mb-1 block">Payment Date <span className="text-[#A87608] text-xs">(for contest)</span></Label>
              <DatePicker
                date={policyForm.payment_date}
                onDateChange={(date) => setPolicyForm(prev => ({ ...prev, payment_date: date }))}
                placeholder="Select payment date"
              />
              <p className="mt-1 text-xs text-[#3A4557]">Date when payment was made (used for contest calculation)</p>
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Policy Term</Label>
              <Select value={String(policyForm.policy_term_years)} onValueChange={handlePolicyTermChange}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10">
                  <SelectValue placeholder="Select term..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  <SelectItem value="1">1 Year (Annual)</SelectItem>
                  <SelectItem value="2">2 Years</SelectItem>
                  <SelectItem value="3">3 Years</SelectItem>
                  <SelectItem value="4">4 Years</SelectItem>
                  <SelectItem value="5">5 Years</SelectItem>
                  <SelectItem value="6">6 Years</SelectItem>
                  <SelectItem value="7">7 Years</SelectItem>
                  <SelectItem value="8">8 Years</SelectItem>
                  <SelectItem value="9">9 Years</SelectItem>
                  <SelectItem value="10">10 Years</SelectItem>
                </SelectContent>
              </Select>
              {policyForm.policy_term_years > 1 && (
                <p className="mt-1 text-xs text-[#A87608]">
                  Multi-year policy: Commissionable premium = ₹{((parseFloat(policyForm.premium_amount) || 0) / policyForm.policy_term_years).toLocaleString('en-IN', { maximumFractionDigits: 0 })}/year
                </p>
              )}
            </div>
            <div>
              <Label className="text-[#3A4557] text-sm">Platform / Broker</Label>
              <Select value={policyForm.platform} onValueChange={handlePlatformChange}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10">
                  <SelectValue placeholder="Select platform..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  {platforms.map((platform) => (
                    <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 mt-2 flex-wrap">
                {!showAddPlatform && !showRenamePlatform && (
                  <button type="button" onClick={() => setShowAddPlatform(true)}
                    className="flex items-center gap-1 text-xs text-[#A87608] hover:text-[#A87608]"
                    data-testid="add-platform-btn">
                    <Plus className="h-3 w-3" /> Add Platform
                  </button>
                )}
                {showAddPlatform && (
                  <div className="flex gap-2 w-full">
                    <Input type="text" value={newPlatformName} onChange={(e) => setNewPlatformName(e.target.value)}
                      placeholder="Platform name" onKeyDown={(e) => e.key === 'Enter' && handleAddPlatform()}
                      className="flex-1 h-8 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                      data-testid="new-platform-input" />
                    <Button type="button" onClick={handleAddPlatform} disabled={addingPlatform}
                      className="h-8 px-3 bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-medium text-xs">
                      {addingPlatform ? "..." : "Add"}
                    </Button>
                    <button type="button" onClick={() => { setShowAddPlatform(false); setNewPlatformName(""); }}
                      className="h-8 px-2 text-[#3A4557] hover:text-[#1B2A4A]">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {showRenamePlatform && (
                  <div className="flex gap-2 w-full">
                    <Input type="text" value={renamePlatformName} onChange={(e) => setRenamePlatformName(e.target.value)}
                      placeholder="New platform name" onKeyDown={(e) => e.key === 'Enter' && handleRenamePlatform()}
                      className="flex-1 h-8 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                      data-testid="rename-platform-input" />
                    <Button type="button" onClick={handleRenamePlatform}
                      className="h-8 px-3 bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-medium text-xs"
                      data-testid="rename-platform-save-btn">
                      Save
                    </Button>
                    <button type="button" onClick={() => { setShowRenamePlatform(false); setRenamePlatformName(""); }}
                      className="h-8 px-2 text-[#3A4557] hover:text-[#1B2A4A]">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {policyForm.platform && !showAddPlatform && !showRenamePlatform && (
                  <div className="flex gap-3 ml-auto">
                    <button type="button" onClick={() => { setRenamePlatformName(policyForm.platform); setShowRenamePlatform(true); }}
                      className="flex items-center gap-1 text-xs text-[#2E3B57] hover:text-[#1B2A4A]"
                      data-testid="rename-platform-btn">
                      <Edit2 className="h-3 w-3" /> Edit
                    </button>
                    <button type="button" onClick={() => handleDeletePlatform(policyForm.platform)}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                      data-testid="delete-platform-btn">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#3A4557] text-sm">Referred By</Label>
              <Select value={policyForm.referred_by} onValueChange={handleReferredByChange}>
                <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10">
                  <SelectValue placeholder="Select referral source..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  {referralSources.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#3A4557] text-sm">Family Members Covered</Label>
              <Input
                type="text"
                name="family_members"
                value={policyForm.family_members}
                onChange={handlePolicyChange}
                placeholder="e.g., Self, Spouse, Child 1, Child 2 (comma separated)"
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-10"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#3A4557] text-sm">Notes</Label>
              <Textarea
                name="notes"
                value={policyForm.notes}
                onChange={handlePolicyChange}
                placeholder="Any additional notes..."
                className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] min-h-[80px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPolicyModal(false)}
              className="border-[#C9D4E6] text-[#2E3B57]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePolicy}
              disabled={savingPolicy || !policyForm.insurance_company || !policyForm.plan_name || !policyForm.policy_number}
              className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A]"
              data-testid="save-policy-btn"
            >
              {savingPolicy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingPolicy ? "Update Policy" : "Add Policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              {deletingPolicy 
                ? `Are you sure you want to delete the policy "${deletingPolicy.plan_name}"? This action cannot be undone.`
                : `Are you sure you want to delete client "${client.name}" and all their policies? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeletingPolicy(null);
              }}
              className="border-[#C9D4E6] text-[#2E3B57]"
            >
              Cancel
            </Button>
            <Button
              onClick={deletingPolicy ? handleDeletePolicy : handleDeleteClient}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
